import { useRef } from "react";
import { Formik } from "formik";
import { FormattedMessage, useIntl } from "react-intl";
import {
  isEmpty,
  isTouchDevice,
  ContextualHelp,
  Column,
  Form,
  Row,
  Checkbox,
  RadioSelect,
  Container,
  TextInput,
  SubmitButton,
  CountriesSelectFormik,
  url,
  useNotifier,
  useSession,
  useBackend,
  useFlipperQuery,
  useTranslateError,
  useBrowser,
} from "lcm-iot-commons";
import validator from "validator";

import { useNavigate } from "react-router-dom-v5-compat";
import { useSignUp } from "../../context/SignUpContext";
import withZxcvbn, { zxcvbnShape } from "../../wrappers/withZxcvbn";
import Captcha from "../Captcha/Captcha";
import PasswordInput from "../Form/PasswordInput";
import SignUpBreadcrumb from "./SignUpBreadcrumb";
import { SignUpTileSlider } from "./SignUpTileSlider";

type Props = {
  zxcvbn: zxcvbnShape;
};

const extractApiErrorMessage = (apiErrors: $TSFixMe) =>
  apiErrors?.errors?.find((error: $TSFixMe) => error?.message)?.message;

export function SignUp({ zxcvbn }: Readonly<Props>) {
  const captcha = useRef();

  const browser = useBrowser();
  const intl = useIntl();
  const navigate = useNavigate();
  const notifier = useNotifier();
  const session = useSession();
  const backend = useBackend();
  const signUp = useSignUp();
  const translateError = useTranslateError();
  const { data: flipper } = useFlipperQuery();
  const authenticityToken =
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") || "";

  const { mutateAsync: registerUser } = backend.post.useMutation(
    "/users/register",
    { onError: () => {} },
  );

  if (session.authenticated) {
    browser.redirectTo((session as $TSFixMe).startUrl);
  }

  const onSubmit = async (values: $TSFixMe, actions: $TSFixMe) => {
    try {
      const newsletterValue =
        values.newsletter === "true" ? "confirmation_pending" : "false";
      const user = {
        email: values.email,
        first_name: values.firstName,
        last_name: values.lastName,
        password: values.password,
        captcha: values.captcha,
        newsletter: newsletterValue,
        address: {
          company_name: values.company,
          country_code: values.country?.code,
          phone: values.phone,
        },
      };
      await registerUser(user);
      signUp.updateSignUpDetails(values);
      navigate(url("/sign_up_success"));
    } catch (error_) {
      const apiErrors = error_ as $TSFixMe;
      const formErrors: $TSFixMe = {};
      if (apiErrors.contains("taken", "email")) {
        formErrors.email = intl.formatMessage({
          id: "api.error.email.taken",
        });
      } else if (apiErrors.contains("invalid_captcha", "captcha")) {
        const invalidCaptchaMessage = intl.formatMessage({
          id: "validation.captcha.invalid",
        });
        formErrors.captcha = invalidCaptchaMessage;
        notifier.showError(invalidCaptchaMessage);
      } else {
        notifier.showError(
          extractApiErrorMessage(apiErrors) ?? translateError(apiErrors),
        );
      }
      (captcha.current as $TSFixMe)?.reset();
      actions.setErrors(formErrors);
      actions.setSubmitting(false);
      actions.setStatus("");
    }
  };

  const validateAddressFields = (values: $TSFixMe, errors: $TSFixMe) => {
    const errorsForAddressFields = errors;
    const phoneRegex = /^\+?[\d\s\-()/]{7,24}$/;
    if (isEmpty(values.company)) {
      errorsForAddressFields.company = intl.formatMessage({
        id: "validation.company.mandatory",
      });
    }

    if (!values.country?.code) {
      errorsForAddressFields.country = intl.formatMessage({
        id: "validation.country.mandatory",
      });
    }

    if (isEmpty(values.phone)) {
      errorsForAddressFields.phone = intl.formatMessage({
        id: "validation.phone.mandatory",
      });
    } else if (values.phone?.length > 25 || !phoneRegex.test(values.phone)) {
      errorsForAddressFields.phone = intl.formatMessage({
        id: "validation.phone.invalid",
      });
    }
    return errorsForAddressFields;
  };

  const validateForm = (values: $TSFixMe) => {
    const errors = {};
    if (isEmpty(values.firstName)) {
      (errors as $TSFixMe).firstName = intl.formatMessage({
        id: "validation.first_name.mandatory",
      });
    }

    if (isEmpty(values.lastName)) {
      (errors as $TSFixMe).lastName = intl.formatMessage({
        id: "validation.last_name.mandatory",
      });
    }

    if (isEmpty(values.email)) {
      (errors as $TSFixMe).email = intl.formatMessage({
        id: "validation.email.mandatory",
      });
    } else if (
      !validator.isEmail(values.email, { allow_utf8_local_part: false })
    ) {
      (errors as $TSFixMe).email = intl.formatMessage({
        id: "validation.email.invalid",
      });
    }

    if (isEmpty(values.password)) {
      (errors as $TSFixMe).password = intl.formatMessage({
        id: "validation.password.mandatory",
      });
    } else {
      const passwordError = zxcvbn.checkPassword(
        values.password,
        zxcvbn.extractUserInputs(values, ["password", "captcha"]),
      ).error;
      if (passwordError) {
        (errors as $TSFixMe).password = passwordError;
      }
    }
    if (!values.captcha) {
      (errors as $TSFixMe).captcha = intl.formatMessage({
        id: "validation.captcha.mandatory",
      });
    }

    if (!values.terms) {
      (errors as $TSFixMe).terms = intl.formatMessage({
        id: "validation.terms.mandatory",
      });
    }

    if (isEmpty(values.newsletter)) {
      (errors as $TSFixMe).newsletter = intl.formatMessage({
        id: "validation.option.invalid",
      });
    }

    if (flipper?.askForAdditionalUserInfo) {
      return validateAddressFields(values, errors);
    }
    return errors;
  };

  const renderForm = (formProps: $TSFixMe) => {
    const { isSubmitting, isValid } = formProps;

    const newletterHelp = (
      <ContextualHelp
        title={intl.formatMessage({ id: "signup.newsletter.title" })}
      >
        <p>{intl.formatMessage({ id: "signup.newsletter.help_info_1" })}</p>
        <p>{intl.formatMessage({ id: "signup.newsletter.help_info_2" })}</p>
      </ContextualHelp>
    );

    const newsletterOptions = [
      {
        value: "true",
        label: intl.formatMessage({ id: "signup.newsletter.yes" }),
        contextualHelp: newletterHelp,
      },
      {
        value: "false",
        label: intl.formatMessage({ id: "signup.newsletter.no" }),
      },
    ];

    const termsLabel = (
      <span>
        <FormattedMessage id="signup.accept_terms" />
        &nbsp;
        <a href="/legal/terms-of-service" target="_new" id="signup_tos_url">
          <FormattedMessage id="signup.terms" />
        </a>
        &nbsp;
        <FormattedMessage id="signup.and" />
        &nbsp;
        <a href="/legal/privacy-policy" target="_new" id="signup_privacy_url">
          <FormattedMessage id="signup.privacy_policy" />
        </a>
      </span>
    );

    return (
      <Form {...formProps}>
        <TextInput
          {...formProps}
          name="firstName"
          label={intl.formatMessage({ id: "label.first_name" })}
          autoFocus={!isTouchDevice()}
          required
        />
        <TextInput
          {...formProps}
          name="lastName"
          label={intl.formatMessage({ id: "label.last_name" })}
          required
        />
        <TextInput
          {...formProps}
          name="email"
          label={intl.formatMessage({ id: "label.email" })}
          required
          autoCorrect="off"
          autoComplete="username"
        />
        <PasswordInput
          {...formProps}
          name="password"
          label={intl.formatMessage({ id: "label.password" })}
          autoComplete="current-password"
          required
        />
        {flipper?.askForAdditionalUserInfo ? (
          <TextInput
            {...formProps}
            id="phone"
            name="phone"
            label={intl.formatMessage({ id: "label.phone" })}
            required
          />
        ) : null}
        <TextInput
          {...formProps}
          id="company"
          name="company"
          label={intl.formatMessage({ id: "label.company" })}
          required={flipper?.askForAdditionalUserInfo}
        />
        <CountriesSelectFormik
          {...formProps}
          id="country"
          name="country"
          label={intl.formatMessage({ id: "label.country" })}
          required={flipper?.askForAdditionalUserInfo}
          values={{ country: {} }}
          all
        />

        <RadioSelect
          {...formProps}
          id="newsletter"
          name="newsletter"
          label="Radio Select"
          options={newsletterOptions}
        />
        <br />
        <Checkbox
          {...formProps}
          id="terms"
          name="terms"
          labelNode={termsLabel}
        />
        <Captcha {...formProps} name="captcha" ref={captcha} />
        <SubmitButton
          id="sign-up"
          text={intl.formatMessage({ id: "signup.button.signup" })}
          fetching={isSubmitting}
          disabled={!isValid}
        />
      </Form>
    );
  };

  return (
    <Container>
      <Row>
        <div className="col-md-12">
          <SignUpBreadcrumb current="sign-up" action="signup" />
        </div>
        <Column sm="12">
          <h1 id="header">
            <FormattedMessage id="signup.header" />
          </h1>
          <p className="space-after">
            {intl.formatMessage({ id: "signup.introduction" })}
          </p>
        </Column>
      </Row>
      {flipper?.loginWithMyEndressHauser ? (
        <Row>
          <Column md="5">
            <p>{intl.formatMessage({ id: "signin.or_sso_provider" })}</p>
            <form
              method="post"
              action="/app/id/api/auth/myendress"
              id="myendress-signin-form"
            >
              <input
                type="hidden"
                name="authenticity_token"
                value={authenticityToken}
              />
              <div className="button-group">
                <button className="btn" type="submit">
                  {intl.formatMessage({ id: "signin.button.signup.myendress" })}
                </button>
              </div>
            </form>
            <br />
          </Column>
        </Row>
      ) : null}
      <Row>
        <Column md="7">
          <Formik
            validate={validateForm}
            onSubmit={onSubmit}
            initialValues={{}}
          >
            {renderForm}
          </Formik>
        </Column>
        <Column md="4">
          <div
            id="signup_customer_feedback"
            className="sign-up-customer-feedback-block"
          >
            <SignUpTileSlider />
          </div>
        </Column>
      </Row>
    </Container>
  );
}

export default withZxcvbn(SignUp);
